import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, RefreshCw, FileText } from "react-feather";
import StatusBadge from "../../../components/common/StatusBadge";
import { MAROON } from "../../../components/common/brandColors";
import { useCreateAmcContractMutation, useUpdateAmcContractMutation } from "../amcApi";

const FREQUENCIES = [
  { value: "MONTHLY", label: "Month" },
  { value: "QUARTERLY", label: "Quarter" },
  { value: "HALF_YEARLY", label: "6 months" },
  { value: "YEARLY", label: "Year" },
];

// The legacy Service Contract form had a single "5 Years" tick, so the durations people
// actually sell are these. "Custom" exposes the end date directly for anything else.
const DURATIONS = [
  { years: 1, label: "1 yr" },
  { years: 2, label: "2 yr" },
  { years: 5, label: "5 yr" },
  { years: null, label: "Custom" },
];

const STATUS_CLASSES = { ACTIVE: "badge-linesuccess", EXPIRED: "badge-linedanger", COMPLETED: "badge-lineinfo", CANCELLED: "badge-linedark" };
const STATUS_LABELS = { ACTIVE: "Active", EXPIRED: "Expired", COMPLETED: "Completed", CANCELLED: "Cancelled" };

const toInput = (v) => (v ? String(v).substring(0, 10) : "");
const addYears = (isoDate, years) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  d.setFullYear(d.getFullYear() + years);
  d.setDate(d.getDate() - 1); // a 1-year contract from 1 Jan ends 31 Dec, not 1 Jan
  return d.toISOString().substring(0, 10);
};

const addDays = (isoDate, days) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().substring(0, 10);
};

// A renewal starts the day after the old one ended - no gap, no overlap.
const nextDay = (isoDate) => addDays(isoDate, 1);

const spanDays = (startIso, endIso) => {
  const a = new Date(`${toInput(startIso)}T00:00:00`);
  const b = new Date(`${toInput(endIso)}T00:00:00`);
  return Math.round((b - a) / 86400000);
};

/**
 * AMC contract entry, deliberately small: customer (from the page), start, duration, how
 * often a service is due, and the amount. Five fields and it saves - covered equipment is
 * optional and lives in its own section below, so nobody has to fill an equipment register
 * before they can record a contract.
 */
const AmcContractSection = ({
  customer,
  branchId,
  contracts,
  selectedGuid,
  onSelect,
  canManage,
  renewFrom = null,
  onCancelRenewal,
}) => {
  const [createContract, { isLoading: isCreating }] = useCreateAmcContractMutation();
  const [updateContract, { isLoading: isUpdating }] = useUpdateAmcContractMutation();

  const existing = contracts.find((c) => c.recordGuid === selectedGuid) || null;
  // "Am I editing?" is answered by whether a contract is SELECTED, not by
  // whether it happens to be present in `contracts` - that list is a separate
  // query which is briefly empty while it refetches (e.g. right after opening a
  // row, or after any AmcContract cache invalidation). Deriving isNew from
  // `existing` alone meant a Save landing in that window inserted a duplicate
  // instead of updating. That is how GOLDEN KEY LTD ended up with
  // AMC-SYSTEMS-000005 and 000006 a minute apart (2026-09-10) - the create's
  // own response read was also failing then (Batch155), which kept the form in
  // create mode; this guard is what stops the same shape of mistake regardless
  // of why the list is momentarily empty.
  const isNew = !selectedGuid;
  const isSelectedButNotLoaded = Boolean(selectedGuid) && !existing;

  const today = new Date().toISOString().substring(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [durationYears, setDurationYears] = useState(1);
  const [endDate, setEndDate] = useState(addYears(today, 1));
  const [frequency, setFrequency] = useState("QUARTERLY");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  // Billing (Batch154). Kept as its own group of state, and its own section
  // below, because "what the AMC is worth / was it invoiced / what have they
  // paid" is a different question from the contract's dates and rhythm.
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [paidAmount, setPaidAmount] = useState("");

  useEffect(() => {
    if (existing) {
      setStartDate(toInput(existing.startDate));
      setEndDate(toInput(existing.endDate));
      setDurationYears(null);
      setFrequency(existing.frequency);
      setAmount(existing.amcAmount ?? "");
      setRemarks(existing.remarks || "");
      setInvoiceNumber(existing.invoiceNumber || "");
      setPaidAmount(existing.paidAmount ?? "");
    } else if (renewFrom) {
      // Carry on from the day after the old contract ended, for the same span, at the same
      // rhythm and price. Everything stays editable - these are the defaults somebody would
      // otherwise retype from the previous contract, not decisions being made for them.
      const from = nextDay(toInput(renewFrom.endDate));
      setStartDate(from);
      setDurationYears(null);
      setEndDate(addDays(from, spanDays(renewFrom.startDate, renewFrom.endDate)));
      setFrequency(renewFrom.frequency);
      setAmount(renewFrom.amcAmount ?? "");
      setRemarks(`Renewal of ${renewFrom.contractNo}`);
      // A renewal is a NEW billing cycle - deliberately do not carry the old
      // contract's invoice number or payment across, or the new one would look
      // paid the moment it is created.
      setInvoiceNumber("");
      setPaidAmount("");
    } else if (!selectedGuid) {
      // Only blank the form for a genuinely NEW contract. Guarded on
      // `selectedGuid` rather than falling through, so a selected contract whose
      // list entry has not arrived yet does not get its form wiped mid-edit.
      setStartDate(today);
      setDurationYears(1);
      setEndDate(addYears(today, 1));
      setFrequency("QUARTERLY");
      setAmount("");
      setRemarks("");
      setInvoiceNumber("");
      setPaidAmount("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGuid, existing?.recordGuid, renewFrom?.recordGuid]);

  // Everything the Billing section needs, derived from the two figures the user
  // actually types. Balance is never stored (see Batch154) - "how much is left"
  // has exactly one correct answer at any moment and it is this subtraction.
  const billing = (() => {
    const gross = amount === "" || amount == null ? null : Number(amount);
    const paid = paidAmount === "" || paidAmount == null ? null : Number(paidAmount);
    const invoiced = invoiceNumber.trim().length > 0;

    if (gross == null) {
      return {
        balanceText: "—",
        balanceColor: "#6c757d",
        label: invoiced ? "Invoiced" : "No amount set",
        badgeClass: "badge-linedark",
        warning: null,
      };
    }

    const balance = gross - (paid || 0);
    const fmt = (n) => Number(n).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Overpayment is allowed rather than blocked - the figures are a record of
    // what happened, and a genuine overpayment shouldn't be un-recordable - but
    // it is called out so it reads as deliberate and not as a typo.
    if (balance < 0) {
      return {
        balanceText: `+${fmt(Math.abs(balance))}`,
        balanceColor: "#B4451F",
        label: "Overpaid",
        badgeClass: "badge-linewarning",
        warning: `Paid ${fmt(paid)} against an AMC of ${fmt(gross)} - overpaid by ${fmt(Math.abs(balance))}.`,
      };
    }
    if (balance === 0 && paid != null) {
      return { balanceText: fmt(0), balanceColor: "#1B7A4B", label: "Paid in full", badgeClass: "badge-linesuccess", warning: null };
    }
    return {
      balanceText: fmt(balance),
      balanceColor: paid ? "#A9750A" : "#6c757d",
      label: !invoiced ? "Not invoiced" : paid ? "Part paid" : "Invoiced, unpaid",
      badgeClass: !invoiced ? "badge-linedark" : paid ? "badge-linewarning" : "badge-linedanger",
      warning: null,
    };
  })();

  const pickDuration = (years) => {
    setDurationYears(years);
    if (years) setEndDate(addYears(startDate, years));
  };

  const changeStart = (value) => {
    setStartDate(value);
    if (durationYears) setEndDate(addYears(value, durationYears));
  };

  const handleSave = async () => {
    if (!customer) {
      toast.error("Choose a customer first.");
      return;
    }
    // A contract is selected but its record has not arrived yet. Saving now
    // could only either crash on `existing` or - the old behaviour - silently
    // insert a duplicate. Neither is acceptable, so refuse and say why.
    if (isSelectedButNotLoaded) {
      toast.error("Still loading this contract - try again in a moment.");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Start and end dates are required.");
      return;
    }
    if (endDate < startDate) {
      toast.error("The end date cannot be before the start date.");
      return;
    }

    try {
      if (isNew) {
        const created = await createContract({
          branchId: Number(branchId ?? customer.branchId),
          custId: customer.custId,
          customerBranchId: customer.branchId,
          locationId: null,
          serviceTypeId: null,
          startDate,
          endDate,
          frequency,
          customIntervalDays: null,
          amcAmount: amount === "" ? null : Number(amount),
          invoiceNumber: invoiceNumber.trim() || null,
          paidAmount: paidAmount === "" ? null : Number(paidAmount),
          remarks: remarks || null,
          // Setting this is what marks the old contract renewed and takes it off the
          // dashboard's pending-renewal list. Server-checked to be the same customer.
          renewedFromContractRecordGuid: renewFrom?.recordGuid ?? null,
        }).unwrap();
        toast.success(
          renewFrom
            ? `${created.contractNo} created, renewing ${renewFrom.contractNo}, with ${created.visitCount} scheduled visit(s).`
            : `${created.contractNo} created with ${created.visitCount} scheduled visit(s).`
        );
        onSelect(created.recordGuid);
      } else {
        await updateContract({
          recordGuid: existing.recordGuid,
          locationId: existing.locationId,
          serviceTypeId: existing.serviceTypeId,
          endDate,
          amcAmount: amount === "" ? null : Number(amount),
          // The reason this path takes them at all: the payment usually arrives
          // long after the contract was created.
          invoiceNumber: invoiceNumber.trim() || null,
          paidAmount: paidAmount === "" ? null : Number(paidAmount),
          status: existing.status,
          remarks: remarks || null,
        }).unwrap();
        toast.success("AMC updated.");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Could not save the AMC.");
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0">
            {renewFrom && !existing ? "Renew AMC" : "AMC Contract"}
            {existing && <span className="ms-2 text-muted fw-normal">{existing.contractNo}</span>}
          </h5>
          <div className="d-flex align-items-center gap-2">
            {contracts.length > 0 && (
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 210 }}
                value={selectedGuid || ""}
                onChange={(e) => onSelect(e.target.value || null)}
              >
                <option value="">+ New AMC</option>
                {contracts.map((c) => (
                  <option key={c.recordGuid} value={c.recordGuid}>
                    {c.contractNo} · {toInput(c.startDate)} to {toInput(c.endDate)}
                  </option>
                ))}
              </select>
            )}
            {existing && (
              <StatusBadge status={existing.effectiveStatus} classMap={STATUS_CLASSES} labelMap={STATUS_LABELS} />
            )}
          </div>
        </div>

        {renewFrom && !existing && (
          <div className="alert alert-success d-flex justify-content-between align-items-center py-2">
            <span>
              Renewing <strong>{renewFrom.contractNo}</strong> ({toInput(renewFrom.startDate)} to{" "}
              {toInput(renewFrom.endDate)}, {renewFrom.visitsDone} of {renewFrom.visitCount} visits done).
              Saving creates a new contract and its own schedule - the old one is kept exactly as it is.
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancelRenewal}>
              Cancel
            </button>
          </div>
        )}

        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Start date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => changeStart(e.target.value)}
              disabled={!isNew || !canManage}
            />
            {!isNew && <small className="text-muted">Fixed once the schedule exists.</small>}
          </div>

          <div className="col-md-3">
            <label className="form-label d-block">Duration</label>
            <div className="btn-group w-100" role="group">
              {DURATIONS.map((d) => (
                <button
                  key={d.label}
                  type="button"
                  className={`btn btn-sm ${durationYears === d.years ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => pickDuration(d.years)}
                  disabled={!canManage}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="col-md-3">
            <label className="form-label">End date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setDurationYears(null);
              }}
              disabled={!canManage}
            />
          </div>

          <div className="col-md-8">
            <label className="form-label d-block">Service every</label>
            <div className="btn-group w-100" role="group">
              {FREQUENCIES.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={`btn btn-sm ${frequency === f.value ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setFrequency(f.value)}
                  disabled={!isNew || !canManage}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {!isNew && <small className="text-muted">Fixed once the schedule exists.</small>}
          </div>

          <div className="col-md-4">
            <label className="form-label">Remarks</label>
            <input
              type="text"
              className="form-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={!canManage}
              placeholder="optional"
            />
          </div>

        </div>

        {/* Billing - its own section on purpose (Batch154, user's own framing:
            "make amount, invoice, paid everything seperate section"). The value
            of the contract, whether it has been invoiced, and what has actually
            been paid are a different question from its dates and service
            rhythm, and they get answered at different times: the amount when
            the deal is agreed, the invoice when it is raised, the payment
            whenever it arrives. All three stay editable for exactly that
            reason. */}
        <div className="border rounded-3 p-3 mt-1 mb-3" style={{ borderColor: `${MAROON}2E` }}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <FileText size={16} style={{ color: MAROON }} />
            <h6 className="mb-0 fw-bold">Billing</h6>
            {!isNew && (
              <span className={`badge ms-1 ${billing.badgeClass}`}>{billing.label}</span>
            )}
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">AMC amount</label>
              <input
                type="number"
                min="0"
                step="any"
                className="form-control"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={!canManage}
                placeholder="0.00"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Invoice number</label>
              <input
                type="text"
                className="form-control"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                disabled={!canManage}
                placeholder="not invoiced yet"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Amount paid</label>
              <input
                type="number"
                min="0"
                step="any"
                className="form-control"
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                disabled={!canManage}
                placeholder="0.00"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Balance</label>
              {/* Derived, never stored and never typed - so it cannot disagree
                  with the two figures above it. */}
              <div
                className="form-control d-flex align-items-center fw-bold"
                style={{ background: "#f8f9fa", color: billing.balanceColor }}
              >
                {billing.balanceText}
              </div>
            </div>
          </div>

          {billing.warning && (
            <small className="d-block mt-2" style={{ color: "#B4451F" }}>
              {billing.warning}
            </small>
          )}
        </div>

        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <button
              type="button"
              className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
              onClick={handleSave}
              disabled={!customer || !canManage || isCreating || isUpdating}
            >
              {isNew && !renewFrom && <Plus size={16} />}
              {isNew && renewFrom && <RefreshCw size={16} />}
              {isCreating || isUpdating
                ? "Saving…"
                : isNew
                ? renewFrom
                  ? "Create Renewal"
                  : "Create AMC"
                : "Save changes"}
            </button>
          </div>
        </div>

        {isNew && customer && (
          <small className="text-muted d-block mt-3">
            Saving generates the visit schedule automatically. Equipment is optional — you can add it below
            afterwards, or not at all.
          </small>
        )}
      </div>
    </div>
  );
};

export default AmcContractSection;
